import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  addPlace,
  deletePlace,
  fetchCategories,
  fetchCountries,
  fetchPlaces,
  updatePlace,
} from "../../utils/apis";
import PlacesManagementDialog from "./components/PlacesModal";
import DataTable from "./placesManagementListTable";
import PlaceFilter from "./components/FilterModal";

const SEARCH_DEBOUNCE_MS = 500;

const PlacesManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [fetch, setFetch] = useState(true);
  const [places, setPlaces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    total: 0,
  });
  const [modalTitle, setModalTitle] = useState("");
  const [editValues, setEditValues] = useState(null);
  const [open, setOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);
  const searchTermRef = useRef(searchTerm);

  const handleFilterOpen = () => {
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  const handleSearch = async (filters) => {
    const { country, location } = filters;

    const page = pagination?.page || 1;
    const perPage = pagination?.perPage || 10;

    try {
      const response = await fetchPlaces(enqueueSnackbar, {
        page,
        perPage,
        country,
        location,
        search: searchTerm || undefined,
      });

      if (response && response._metadata && response._metadata.pagination) {
        setPlaces(response.data);
        const paginationData = response._metadata.pagination;
        setPagination({
          page: paginationData.page || page,
          perPage: paginationData.perPage || perPage,
          total: paginationData.total || 0,
        });
      }
    } catch (error) {
      console.error("Error during search:", error);
    }
  };

  const handleSearchChange = useCallback((value) => {
    searchTermRef.current = value;
    setSearchTerm(value);
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      setFetch(true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const handleClickOpen = () => {
    setModalTitle("Add Place");
    setOpen(true);
  };

  const handleModalEditClick = (id, data) => {
    setModalTitle("Edit Place");
    setOpen(true);
    setEditValues({
      id,
      ...data,
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    (async function () {
      const result = await fetchCountries();
      if (result.statusCode === 200) {
        setCountries(
          result.data.countries?.filter((country) => country.status === true)
        );
      }
    })();
  }, [setCountries]);

  useEffect(() => {
    (async function () {
      const result = await fetchCategories();
      if (result.statusCode === 200) {
        setCategories(
          result.data.categories?.filter((country) => country.status === true)
        );
      }
    })();
  }, [setCountries]);

  const handlePlaceEdit = async (id, data) => {
    try {
      const result = await updatePlace(id, data, enqueueSnackbar);
      if (result?.statusCode === 200) {
        const updatedPlace = result?.data;
        setPlaces((prevPlaces) => {
          const index = prevPlaces.findIndex(
            (place) => place?.shortId === updatedPlace?.shortId
          );
          if (index > -1) {
            const updatedPlaces = [...prevPlaces];
            updatedPlaces[index] = updatedPlace;
            return updatedPlaces;
          }
          return prevPlaces;
        });
      }
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(`${error?.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    }
  };

  const handleAddPlace = async (data) => {
    try {
      const result = await addPlace(data, enqueueSnackbar);
      setPlaces([result.data?.place, ...places]);
    } catch (error) {
      error &&
        enqueueSnackbar(`${error?.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
    }
  };

  const handleDelete = async (ids = []) => {
    try {
      const result = await deletePlace(ids, enqueueSnackbar);
      if (result?.statusCode === 200) {
        setPlaces((prev) => {
          const remainingPlaces = prev.filter(
            (place) => !ids.some((id) => place.shortId === id)
          );
          if (remainingPlaces.length === 0) {
            setFetch(true);
          } else {
            return remainingPlaces;
          }
        });
      }
    } catch (error) {
      enqueueSnackbar(`${error?.message}`, {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setPagination((prev) => ({
      ...prev,
      perPage: event.target.value,
      page: 0,
    }));
    setFetch(true);
  };

  // Handle page change
  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setFetch(true);
  };

  // Main data fetch — includes search term so pagination + search work together
  useEffect(() => {
    if (fetch) {
      (async function () {
        const result = await fetchPlaces(enqueueSnackbar, {
          page: pagination.page,
          perPage: pagination.perPage,
          search: searchTermRef.current || undefined,
        });
        if (result.statusCode === 200) {
          const {
            data,
            _metadata: {
              pagination: { page, perPage, total },
            },
          } = result;
          setPlaces(data);
          setPagination({
            page,
            perPage,
            total,
          });
        }
      })();
    }
    return setFetch(false);
  }, [fetch, enqueueSnackbar, pagination.page, pagination.perPage]);

  return (
    <div>
      <DataTable
        onAdd={handleClickOpen}
        onEdit={handleModalEditClick}
        Places={places}
        onDelete={handleDelete}
        onChangeStatus={() => {}}
        pagination={pagination}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        onFilter={handleFilterOpen}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />
      <PlacesManagementDialog
        countries={countries}
        categories={categories}
        editValues={editValues}
        setEditValues={setEditValues}
        onAdd={handleAddPlace}
        onEdit={handlePlaceEdit}
        open={open}
        handleClose={handleClose}
        title={modalTitle}
      />
      <PlaceFilter
        open={filterOpen}
        onClose={handleFilterClose}
        onSearch={handleSearch}
      />
    </div>
  );
};

export default PlacesManagement;
